// Lightweight Charts candle pane + RSI sub-pane stitched into one container. The two
// charts share a horizontal time scale via subscribeVisibleTimeRangeChange — pan/zoom
// on either pane keeps both aligned. Markers (↑ for long, ↓ for short) are drawn on
// the candle series at each alert's signalBarTime.
//
// Library API surface used: createChart, addSeries(CandlestickSeries|LineSeries|BaselineSeries),
// setData, update, setMarkers, timeScale().subscribeVisibleTimeRangeChange + setVisibleRange.

import { useEffect, useRef } from "react";
import {
  createChart,
  type IChartApi,
  type ISeriesApi,
  type Time,
  CandlestickSeries,
  LineSeries,
  createSeriesMarkers,
  type ISeriesMarkersPluginApi,
  type SeriesMarker,
} from "lightweight-charts";
import type { AlertSummaryDto, BacktestDetailDto, BacktestResponseDto, CandleDto } from "../../api/types";
import { computeRsi } from "../../hooks/rsi";
import { toChartTime } from "../../hooks/time";
import { exitMeta } from "../backtest/primary";

interface Props {
  candles: CandleDto[];
  alerts: AlertSummaryDto[];
  rsiPeriod: number;
  /** Optional backtest result. Long/short entries are overlaid as direction arrows. */
  backtest?: BacktestResponseDto | null;
  /**
   * Heavy render payload for the backtest: the EXACT candle window the engine evaluated +
   * the EXACT per-bar indicator series it computed. When present the chart renders THIS
   * verbatim (candles + the engine's own RSI series) so it's a faithful simulation of the
   * backend — never a client-side recomputation. When absent (pure live view) the chart
   * shows the live candle stream and a client-computed RSI clearly labelled as a live
   * approximation.
   */
  backtestDetail?: BacktestDetailDto | null;
}

export function ChartPanel({ candles, alerts, rsiPeriod, backtest, backtestDetail }: Props) {
  const priceContainer = useRef<HTMLDivElement | null>(null);
  const rsiContainer = useRef<HTMLDivElement | null>(null);
  const priceChart = useRef<IChartApi | null>(null);
  const rsiChart = useRef<IChartApi | null>(null);
  const candleSeries = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const candleMarkers = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const rsiSeries = useRef<ISeriesApi<"Line"> | null>(null);

  // Initial chart setup. Re-runs only on mount/unmount; subsequent data flows happen in
  // separate effects below so we don't tear down the chart on every prop change.
  useEffect(() => {
    if (!priceContainer.current || !rsiContainer.current) return;

    priceChart.current = createChart(priceContainer.current, {
      autoSize: true,
      layout: { background: { color: "#0d1117" }, textColor: "#c9d1d9" },
      grid: { vertLines: { color: "#161b22" }, horzLines: { color: "#161b22" } },
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: "#30363d" },
      rightPriceScale: { borderColor: "#30363d" },
      crosshair: { mode: 0 },
    });
    candleSeries.current = priceChart.current.addSeries(CandlestickSeries, {
      upColor: "#3fb950",
      downColor: "#f85149",
      borderVisible: false,
      wickUpColor: "#3fb950",
      wickDownColor: "#f85149",
    });
    candleMarkers.current = createSeriesMarkers(candleSeries.current, []);

    rsiChart.current = createChart(rsiContainer.current, {
      autoSize: true,
      layout: { background: { color: "#0d1117" }, textColor: "#c9d1d9" },
      grid: { vertLines: { color: "#161b22" }, horzLines: { color: "#161b22" } },
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: "#30363d" },
      rightPriceScale: { borderColor: "#30363d" },
      crosshair: { mode: 0 },
    });
    rsiSeries.current = rsiChart.current.addSeries(LineSeries, {
      color: "#58a6ff",
      lineWidth: 2,
    });
    // Reference lines at 30/50/70 — the bands the RSI condition generators key off.
    [
      { value: 30, color: "#3fb950" },
      { value: 50, color: "#8b949e" },
      { value: 70, color: "#f85149" },
    ].forEach(({ value, color }) => {
      rsiSeries.current!.createPriceLine({
        price: value,
        color,
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: String(value),
      });
    });

    // Sync horizontal pan/zoom across the two charts.
    const priceTs = priceChart.current.timeScale();
    const rsiTs = rsiChart.current.timeScale();
    let syncing = false;
    const sync = (source: IChartApi, target: IChartApi) => {
      if (syncing) return;
      const range = source.timeScale().getVisibleLogicalRange();
      if (!range) return;
      syncing = true;
      target.timeScale().setVisibleLogicalRange(range);
      // microtask reset so the reciprocal handler doesn't loop
      Promise.resolve().then(() => { syncing = false; });
    };
    priceTs.subscribeVisibleLogicalRangeChange(() => sync(priceChart.current!, rsiChart.current!));
    rsiTs.subscribeVisibleLogicalRangeChange(() => sync(rsiChart.current!, priceChart.current!));

    return () => {
      priceChart.current?.remove();
      rsiChart.current?.remove();
      priceChart.current = null;
      rsiChart.current = null;
      candleSeries.current = null;
      candleMarkers.current = null;
      rsiSeries.current = null;
    };
  }, []);

  // Data updates: candles + RSI sub-pane.
  //
  // SOURCE OF TRUTH RULE: when a backtest detail is present we render the engine's EXACT
  // candle window and the engine's EXACT RSI series, index-aligned, with NO client recompute
  // and NO independent re-sort (the backend returns it chronological and the indicator values
  // are aligned to that order — re-sorting would misalign values to candles). Only the pure
  // live view (no detail) falls back to a client-computed RSI, clearly an approximation.
  useEffect(() => {
    if (!candleSeries.current || !rsiSeries.current) return;

    if (backtestDetail && backtestDetail.candles.length > 0) {
      // ── Backend-truth render ──────────────────────────────────────────────
      const c = backtestDetail.candles; // already chronological; do NOT re-sort
      candleSeries.current.setData(c.map((k) => ({
        time: (toChartTime(k.openTime)) as Time,
        open: k.open, high: k.high, low: k.low, close: k.close,
      })));

      // Plot the engine's own RSI series verbatim (first column of the first `rsi`
      // instance). null entries are warmup/no-value → skipped, not interpolated.
      const rsiInd = backtestDetail.indicators.find((ind) => ind.baseId === "rsi");
      const rsiCol = rsiInd?.columns[0];
      const rsiData = rsiCol
        ? c
            .map((k, i) => ({ time: (toChartTime(k.openTime)) as Time, value: rsiCol.values[i] }))
            .filter((p): p is { time: Time; value: number } => p.value != null && Number.isFinite(p.value))
        : [];
      rsiSeries.current.setData(rsiData);
      return;
    }

    // ── Live approximation (no backtest detail) ───────────────────────────────
    if (candles.length === 0) return;
    const sorted = [...candles].sort(
      (a, b) => Date.parse(a.openTime) - Date.parse(b.openTime),
    );
    candleSeries.current.setData(sorted.map((c) => ({
      time: (toChartTime(c.openTime)) as Time,
      open: c.open, high: c.high, low: c.low, close: c.close,
    })));

    const closes = sorted.map((c) => c.close);
    const rsi = computeRsi(closes, rsiPeriod);
    const rsiData = sorted
      .map((c, i) => ({ time: (toChartTime(c.openTime)) as Time, value: rsi[i] }))
      .filter((p) => Number.isFinite(p.value));
    rsiSeries.current.setData(rsiData);
  }, [candles, rsiPeriod, backtestDetail]);

  // Alert markers (live + backtest combined). Three visual roles, distinct at-a-glance:
  //   • Live alerts        → big saturated arrows with LONG/SHORT text labels
  //   • Netting entries    → small green/red triangles (arrowUp long / arrowDown short)
  //   • Netting exits      → small squares colour-coded by exit reason (SL=red, TP=green,
  //                          trailing=amber, reverse=blue, bars/indicator=grey, end)
  //
  // The netting backtest (result.strategy) is a faithful TradingView strategy tester, so
  // its exits/reversals ARE shown here — that's the whole point of seeing where SAR flips
  // and SL/TP stops fired. Live alerts remain entry-only and stay visually dominant
  // (bigger, text-labelled) so the production entry-only contract is still legible.
  // Falls back to the legacy long/short scan entries when no netting result is present.
  useEffect(() => {
    if (!candleMarkers.current) return;

    const liveMarkers: SeriesMarker<Time>[] = alerts.map((a) => ({
      time: (toChartTime(a.signalBarTime)) as Time,
      position: a.direction === "long" ? "belowBar" : "aboveBar",
      color: a.direction === "long" ? "#3fb950" : "#f85149",
      shape: a.direction === "long" ? "arrowUp" : "arrowDown",
      text: a.direction.toUpperCase(),
      size: 2,
    }));

    // barTimes[barIdx] is the true signal/exit timestamp in the backtest's OWN window;
    // never index the chart's separate candle array with it. Place by time —
    // lightweight-charts simply won't render markers outside the visible window.
    const at = (barTimes: readonly string[], barIdx: number): Time | null => {
      const ts = barIdx >= 0 && barIdx < barTimes.length ? barTimes[barIdx] : undefined;
      return ts ? (toChartTime(ts) as Time) : null;
    };

    const backtestMarkers: SeriesMarker<Time>[] = [];
    const s = backtest?.strategy;
    if (s) {
      // Cap markers to the most-recent N trades, and drop the per-exit text label above a
      // threshold — otherwise a high-frequency strategy buries the price under thousands of
      // overlapping markers (same fix as the preview chart). Arrows still show within the cap.
      const MARKER_TRADE_CAP = 120;
      const LABEL_LIMIT = 40;
      const total = s.tradeBars.length;
      const start = Math.max(0, total - MARKER_TRADE_CAP);
      const withText = total - start <= LABEL_LIMIT;
      for (let i = start; i < total; i++) {
        const dir = s.directions[i];
        const entT = at(s.barTimes, s.tradeBars[i]);
        if (entT !== null) {
          backtestMarkers.push({
            time: entT,
            position: dir === "long" ? "belowBar" : "aboveBar",
            color: dir === "long" ? "rgba(63,185,80,0.92)" : "rgba(248,81,73,0.92)",
            shape: dir === "long" ? "arrowUp" : "arrowDown",
            text: "",
            size: 1.1,
          });
        }
        const meta = exitMeta(s.exitTypes[i]);
        const exT = at(s.barTimes, s.exitBars[i]);
        if (exT !== null) {
          backtestMarkers.push({
            time: exT,
            // Exit sits opposite the entry so an entry/exit pair brackets the move.
            position: dir === "long" ? "aboveBar" : "belowBar",
            color: meta.color,
            shape: "square",
            text: withText ? meta.short : "",
            size: 1,
          });
        }
      }
    } else {
      // Legacy fallback: isolated long/short scan entries (no exit types available).
      const pushEntries = (direction: "long" | "short", tradeBars: readonly number[], barTimes: readonly string[]) => {
        const color = direction === "long" ? "rgba(63,185,80,0.92)" : "rgba(248,81,73,0.92)";
        for (let i = 0; i < tradeBars.length; i++) {
          const t = at(barTimes, tradeBars[i]);
          if (t === null) continue;
          backtestMarkers.push({
            time: t,
            position: direction === "long" ? "belowBar" : "aboveBar",
            color,
            shape: direction === "long" ? "arrowUp" : "arrowDown",
            text: "",
            size: 1.2,
          });
        }
      };
      if (backtest?.long) pushEntries("long", backtest.long.tradeBars, backtest.long.barTimes);
      if (backtest?.short) pushEntries("short", backtest.short.tradeBars, backtest.short.barTimes);
    }

    const markers = [...liveMarkers, ...backtestMarkers];
    // setMarkers expects ascending time; sort to be safe regardless of input order.
    markers.sort((a, b) => (a.time as number) - (b.time as number));
    candleMarkers.current.setMarkers(markers);
    // Markers derive only from alerts + backtest (placed by time). `candles` is intentionally
    // NOT a dep — it changes every live tick and would needlessly re-run setMarkers.
  }, [alerts, backtest]);

  // Compute legend visibility — only show backtest-related items in the legend if a
  // backtest has been run (otherwise the user sees a noisy key for markers they don't have).
  const hasBacktest = !!(backtest && ((backtest.strategy?.trades ?? 0) > 0 || (backtest.long?.trades ?? 0) > 0 || (backtest.short?.trades ?? 0) > 0));
  const hasNetting = !!(backtest?.strategy && backtest.strategy.trades > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, height: "100%" }}>
      <MarkerLegend hasBacktest={hasBacktest} hasNetting={hasNetting} hasAlerts={alerts.length > 0} />
      <div ref={priceContainer} style={{ flex: 3, minHeight: 0, border: "1px solid #30363d", borderRadius: 6 }} />
      <div ref={rsiContainer} style={{ flex: 1, minHeight: 100, border: "1px solid #30363d", borderRadius: 6 }} />
    </div>
  );
}

/** Inline legend strip explaining each marker type. Only shows rows for layers that exist. */
function MarkerLegend({ hasBacktest, hasNetting, hasAlerts }: { hasBacktest: boolean; hasNetting: boolean; hasAlerts: boolean }) {
  if (!hasBacktest && !hasAlerts) return null;
  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 12,
      padding: "6px 10px",
      background: "#0d1117",
      border: "1px solid #30363d",
      borderRadius: 4,
      fontSize: 11,
      color: "#8b949e",
      alignItems: "center",
    }}>
      <span style={{ fontWeight: 600, color: "#c9d1d9", textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10 }}>
        Markers
      </span>
      {hasAlerts && (
        <>
          <LegendItem icon="↑" iconColor="#3fb950" label="LONG live alert" bold />
          <LegendItem icon="↓" iconColor="#f85149" label="SHORT live alert" bold />
        </>
      )}
      {hasBacktest && (
        <>
          <LegendItem icon="▲" iconColor="#3fb950" label="long entry" />
          <LegendItem icon="▼" iconColor="#f85149" label="short entry" />
        </>
      )}
      {hasNetting && (
        <>
          <LegendItem icon="■" iconColor="#f85149" label="SL exit" />
          <LegendItem icon="■" iconColor="#3fb950" label="TP exit" />
          <LegendItem icon="■" iconColor="#d29922" label="trailing exit" />
          <LegendItem icon="■" iconColor="#58a6ff" label="reverse (SAR)" />
          <LegendItem icon="■" iconColor="#8b949e" label="bars/indicator exit" />
        </>
      )}
    </div>
  );
}

function LegendItem({ icon, iconColor, label, bold }: { icon: string; iconColor: string; label: string; bold?: boolean }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ color: iconColor, fontWeight: bold ? 800 : 600, fontSize: bold ? 14 : 12, lineHeight: 1 }}>
        {icon}
      </span>
      <span style={{ color: "#8b949e" }}>{label}</span>
    </span>
  );
}
